<template>
  <div id="analysis-app" class="analysisPage flex flex-col items-center min-h-screen p-6 mx-auto bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-700 relative">
    <!-- Animated Background Elements -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
      <div class="absolute w-96 h-96 bg-white opacity-10 rounded-full -top-20 -left-20 animate-pulse-slow"></div>
      <div class="absolute w-80 h-80 bg-blue-300 opacity-10 rounded-full top-40 right-10 animate-float"></div>
      <div class="absolute w-64 h-64 bg-purple-300 opacity-10 rounded-full bottom-20 left-40 animate-float-delayed"></div>
    </div>
    
    <!-- Header -->
    <div class="mb-6 w-full relative z-10">
      <router-link to="/" class="no-underline text-white">
        <div class="logo-text-chat flex items-center">
          <span class="text-white">inter<span class="text-yellow-300">bot</span></span>
          <div class="ml-2 bg-yellow-300 p-1 rounded-md">
            <span class="text-xs font-bold text-blue-700">IA</span>
          </div>
        </div>
      </router-link>
    </div>
    
    <!-- Title -->
    <div class="relative z-10 mb-8 text-center">
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-2">Análisis de tu Entrevista</h2>
      <p class="text-white text-opacity-80">Un reporte detallado de tu desempeño</p>
      <h4 class="mt-2 text-yellow-300 font-medium" v-if="chatSessionId && !isPractice">{{ chatSessionId }}</h4>
    </div>
    
    <!-- Loading State -->
    <div v-if="loadingAnalysis" class="relative z-10 w-full max-w-4xl bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-2xl border border-white border-opacity-20 shadow-xl text-center text-white mb-8">
      <div class="flex flex-col items-center">
        <svg class="animate-spin h-10 w-10 text-yellow-300 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-xl">Generando análisis de tu entrevista...</p>
        <p class="text-sm mt-2 text-white text-opacity-70">Esto puede tomar unos momentos</p>
      </div>
    </div>
    
    <!-- Error State -->
    <div v-if="fetchError" class="relative z-10 w-full max-w-4xl bg-red-900 bg-opacity-20 backdrop-blur-sm p-6 rounded-2xl border border-red-400 border-opacity-30 shadow-xl text-center text-white mb-8">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-4 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-lg font-medium mb-2">Error al cargar el análisis</p>
      <p class="text-sm text-white text-opacity-80">{{ fetchError }}</p>
    </div>
    
    <!-- Practice Video and Feedback -->
    <div v-if="isPractice" class="relative z-10 w-full max-w-4xl">
      <div class="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-2xl border border-white border-opacity-20 shadow-xl mb-8">
        <h3 class="text-2xl font-bold text-white mb-6 text-center">¡Has completado tu entrevista de práctica!</h3>
        
        <!-- Video Player with Glow Effect -->
        <div class="w-full max-w-xs mx-auto mb-8 relative">
          <div class="absolute -inset-1 bg-gradient-to-r from-yellow-300 to-pink-500 rounded-xl blur opacity-70 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse-slow"></div>
          <div class="relative">
            <div v-if="!videoPlaying" class="relative rounded-xl overflow-hidden aspect-[9/16] shadow-2xl" @click="playVideo">
              <img src="../assets/vertical-placeholder.png" alt="Video Thumbnail" class="w-full block transform transition duration-500 hover:scale-105"/>
              <button class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex justify-center items-center cursor-pointer transition-all hover:scale-110 hover:bg-opacity-30 border-2 border-white border-opacity-40">
                <div class="w-0 h-0 border-t-[15px] border-b-[15px] border-l-[25px] border-transparent border-l-white ml-2"></div>
              </button>
            </div>
            <video v-if="videoPlaying" ref="verticalVideoPlayer" :src="verticalVideoSource" controls class="w-full block aspect-[9/16] rounded-xl shadow-2xl"></video>
          </div>
        </div>
        
        <!-- Feedback Buttons -->
        <h3 class="text-xl font-bold text-white mb-4 text-center">¿Te resultó útil nuestra plataforma?</h3>
        <div class="flex justify-center space-x-6">
          <!-- "Sí" Button -->
          <button
            class="flex items-center px-6 py-3 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white rounded-full transition-all duration-300 focus:outline-none shadow-md hover:shadow-lg transform hover:scale-105 hover:-translate-y-1"
            :class="{ 'animate-pulse': feedbackLoading === 'Si' }"
            @click="submitFeedback('Si')"
            :disabled="feedbackLoading"
          >
            <img src="../assets/SiRecomiendo.png" alt="Si Recomiendo" class="w-8 h-8 mr-3 bg-white rounded-full p-1" />
            <span v-if="feedbackLoading != 'Si'" class="font-bold">¡Sí, me gustó!</span>
            <span v-else class="font-bold">Enviando...</span>
          </button>
          
          <!-- "No" Button -->
          <button
            class="flex items-center px-6 py-3 bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white rounded-full transition-all duration-300 focus:outline-none shadow-md hover:shadow-lg transform hover:scale-105 hover:-translate-y-1"
            :class="{ 'animate-pulse': feedbackLoading === 'No' }"
            @click="submitFeedback('No')"
            :disabled="feedbackLoading"
          >
            <img src="../assets/NoRecomiendo.png" alt="No Recomiendo" class="w-8 h-8 mr-3 bg-white rounded-full p-1" />
            <span v-if="feedbackLoading != 'No'" class="font-bold">No me ayudó</span>
            <span v-else class="font-bold">Enviando...</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Analysis Results -->
    <div v-if="chatSessionId && analysisResult" class="relative z-10 w-full max-w-4xl">
      <!-- Summary Section -->
      <AnalysisSection v-if="analysisResult.evaluationSummary" title="Resumen" :content="analysisResult.evaluationSummary" />
      
      <!-- Score Section -->
      <AnalysisScore v-if="analysisResult.score" :score="analysisResult.score" />
      
      <!-- Personality Table -->
      <PersonalityTable v-if="analysisResult.personalityAssessment && analysisResult.personalityAssessment.length > 0" :tableData="analysisResult.personalityAssessment" />
      
      <!-- Development Suggestions -->
      <AnalysisSuggestions v-if="analysisResult.developmentSuggestions && analysisResult.developmentSuggestions.length > 0" 
        title="Sugerencias de Desarrollo" 
        :suggestions="analysisResult.developmentSuggestions" />
      
      <!-- Interview Performance Suggestions -->
      <AnalysisSuggestions v-if="analysisResult.interviewPerformanceSuggestions && analysisResult.interviewPerformanceSuggestions.length > 0" 
        title="Sugerencias para Mejorar Desempeño en la Entrevista" 
        :suggestions="analysisResult.interviewPerformanceSuggestions" />
    </div>
    
    <!-- No Analysis Available -->
    <div v-else-if="!loadingAnalysis && !fetchError" class="relative z-10 w-full max-w-4xl bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-2xl border border-white border-opacity-20 shadow-xl text-center text-white mb-8">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-4 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-xl font-medium mb-2">No hay un análisis disponible</p>
      <p class="text-white text-opacity-80">No se ha encontrado análisis para esta entrevista.</p>
    </div>
    
    <!-- Navigation Buttons -->
    <div class="mt-8 flex gap-4 justify-center relative z-10">
      <button @click="goToInterview" 
        class="bg-white bg-opacity-20 backdrop-blur-sm hover:bg-opacity-30 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 hover:shadow-lg transform hover:scale-105 hover:-translate-y-1 shadow-md border border-white border-opacity-20">
        Ver Entrevista
      </button>
      <button v-if="!isPractice" @click="goBack" 
        class="bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 hover:shadow-lg transform hover:scale-105 hover:-translate-y-1 shadow-md">
        Volver
      </button>
    </div>
  </div>
</template>
  
  <script>
  import axios from 'axios';
  import { auth, db } from '@/firebase';
  import { doc, collection, getDoc } from "firebase/firestore";
  import AnalysisSection from '../components/AnalysisSection.vue';
  import AnalysisSuggestions from '../components/AnalysisSuggestions.vue';
  import AnalysisScore from '../components/AnalysisScore.vue';
  import PersonalityTable from '../components/PersonalityTable.vue';
  import verticalVideo from '../assets/FinEntrevistaINTERBOT.mp4';
  import { nextTick } from 'vue';
  
  export default {
    name: 'AnalysisView',
    components: {
      AnalysisSection,
      AnalysisSuggestions,
      AnalysisScore,
      PersonalityTable
    },
    data() {
      return {
        chatSessionId: this.$route.query.code,
        analysisResult: null,
        loadingAnalysis: true,
        fetchError: null,
        isPractice: false,
        verticalVideoSource: verticalVideo,
        verticalVideoPlayer: null,
        videoPlaying: false,
        feedbackLoading: null,
      };
    },
    async mounted() {
      if(!this.chatSessionId){
        this.fetchError = "chatSessionId is not available"
        this.loadingAnalysis = false;
        return;
      }
      if(this.videoPlaying) {
        this.playVideo();
      }
      await this.fetchAnalysis();
    },
    methods: {
      playVideo: async function() {
        this.videoPlaying = true;
        await nextTick();
        if (this.$refs.verticalVideoPlayer) {
          this.$refs.verticalVideoPlayer.play();
        }
      },
        async fetchAnalysis() {
            let interviewRef = null;
            let docSnap = null;
          try {
             if (this.chatSessionId) {
                   let token = null;
                   const interviewDocRef = doc(db, "interviews", this.chatSessionId);
                    console.log("interviewDocRef (interviews)", interviewDocRef)
                   docSnap = await getDoc(interviewDocRef);
                     if(docSnap.exists()){
                        this.isPractice = true;
                          interviewRef = interviewDocRef;
                     }else {
                         interviewRef = doc(collection(db, "company_interviews"), this.chatSessionId);
                           console.log("interviewRef (company_interviews)", interviewRef)
                             const docSnap2 = await getDoc(interviewRef);
                             if(docSnap2.exists()){
                                console.log("docSnap2 data", docSnap2.data());
                            }
                   }
                if(!this.isPractice && auth.currentUser){
                       token = await auth.currentUser.getIdToken()
                   }
                   const response = await axios.get(`/api/chat/${this.chatSessionId}`, {
                      headers: { Authorization: this.isPractice ? null : `Bearer ${token}` }
                    });
                    if(!response.status === 200){
                       const errorData = response.data;
                         throw new Error(
                           `HTTP error! status: ${response.status} ${JSON.stringify(errorData)}`
                       );
                    }
                   const analysis = response.data.analysis;
                   if (analysis) {
                         this.analysisResult = this.parseAnalysis(analysis);
                       if(!this.analysisResult){
                             this.fetchError = "There was an error parsing the Analysis, please review the logs";
                        }
                     } else {
                        this.analysisResult = null;
                        this.fetchError =  "No analysis was found";
                   }
                }
             } catch (error) {
                console.error("Error al analizar la entrevista", error);
                  this.fetchError = error.message;
           } finally {
               this.loadingAnalysis = false;
           }
       },
        parseAnalysis(analysis) {
           if (!analysis){
               console.log("analysis is null");
              return null;
           }
           try{
               let parsedAnalysis = analysis;
                if(typeof analysis === 'string'){
                     // Removemos delimitadores y espacios
                     const cleanedAnalysis = analysis.trim().replace(/^```(json)?\s*|```\s*$/g, '');
  
                          try{
                             parsedAnalysis = JSON.parse(cleanedAnalysis); // Intentamos hacer parse si es un json string
                         } catch(e) {
                               console.log("analysis is a string, but is not JSON");
                               console.log(e);
                               // Si el string no es un JSON válido, retornamos null
                               return null;
                        }
                  }
                   if(typeof parsedAnalysis === 'object' && parsedAnalysis !== null){
                       const personalityArray = Object.entries(parsedAnalysis.personalityAssessment || {}).map(([aspect, {evaluation, justification}]) => ({
                           aspect,
                            evaluation,
                           justification
                        }));
                         return {
                           evaluationSummary: parsedAnalysis.evaluationSummary,
                             score: parsedAnalysis.score ? String(parsedAnalysis.score) : null ,
                          personalityAssessment: personalityArray,
                             developmentSuggestions: parsedAnalysis.developmentSuggestions,
                            interviewPerformanceSuggestions: parsedAnalysis.interviewPerformanceSuggestions
                        }
                   }
                console.log("analysis is a unknown format");
                return null; // Si llegamos aqui, es que no sabemos parsear lo que llegó
           } catch (e){
                console.log("Error parsing analysis:", e);
             return null;
          }
      },
       goToInterview() {
            this.$router.push({ path: '/interbot?code=', query: { code: this.chatSessionId } });
        },
        goBack() {
           this.$router.back()
      },
      async submitFeedback(feedback) {
        this.feedbackLoading = feedback;
          try {
            const response = await axios.post('/api/submit-feedback', {
              chatSessionId: this.chatSessionId,
              feedback: feedback,
            });
            if (response.status === 201) {
                 console.log('Feedback submitted successfully!');
              // Optionally, provide feedback to the user, or disable the buttons
             // For now, just log the success.
            } else {
              console.error('Failed to submit feedback:', response.status, response.data);
            }
           } catch (error) {
            console.error('Error submitting feedback:', error);
          } finally {
             this.feedbackLoading = null;
           }
      },
      
     },
  };
  </script>
  
<style scoped>
@keyframes pulse-slow {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 0.3;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes float-delayed {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
}

.animate-pulse-slow {
  animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 7s ease-in-out infinite;
  animation-delay: 2s;
}

/* Apply blur effect for glass morphism */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Highlight text with gradient */
.bg-clip-text {
  background-clip: text;
  -webkit-background-clip: text;
}

/* Enhanced button hover effects */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Improved card hover animations */
.hover\:scale-105:hover {
  transform: scale(1.05);
}

.hover\:-translate-y-1:hover {
  transform: translateY(-4px);
}
</style>